import SearchableSelect from '../../components/ui/SearchableSelect';
import PermissionButton from "../../components/ui/PermissionButton";
import { UNIT_OPTIONS } from "../../utils/units";
import { useEffect, useRef, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useAuth } from "../Context/AuthContext";
import Select from 'react-select';
import DepartmentSelect from '../../components/ui/DepartmentSelect';

const DEFAULT_TERMS = `1. Prices must be inclusive of all taxes unless specified.
2. Delivery must be on or before the expected date.
3. Quality rejection will be returned at vendor cost.
4. Payment terms: 15 days from delivery.
`;

const createEmptyRow = () => ({
  item_id: "",
  item: "",
  category: "",
  spec: "",
  qty: "",
  unit: "Kg",
  quality: "",
  packaging: "",
  brand: "",
  rate: "",
  tax_percent: "0",
});

export default function CreateRFQPage() {
  const [rows, setRows] = useState([createEmptyRow()]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorSearch,setVendorSearch]=useState('');
  const [notes, setNotes] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [branches, setBranches] = useState([]);
  const [location, setLocation] = useState("");
  const [departmentId,setDepartmentId]=useState('');
  const [priority, setPriority] = useState("Normal");
  const [poType,setPoType]=useState('quotation');
  const submittingRef=useRef(false);
  const [submitting,setSubmitting]=useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const { user, branchId } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    vendor_type_id: "", // ✅ IMPORTANT
    gst: "",
    pan: "",
    status: "Active",
    contacts: [{ name: "", phone: "", email: "", primary: true }],
    branches: [],
    notes: "",
  });

  useEffect(() => {
    if (user?.role === "staff" && branchId) {
      setLocation(Number(branchId));
      setForm((prev) => ({
        ...prev,
        branches: [Number(branchId)],
      }));
    }
  }, [user?.role, branchId]);

  useEffect(() => {
    // vendors
    fetch("/api/vendors", {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setVendors(data.data || []));

    fetch("/api/vendor-types", {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setVendorTypes(data.data || []));

    // 🔥 branches
    fetch("/api/branches", {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setBranches(data.data || []));

    fetch("/api/inventory/raw-materials", {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setRawMaterials(data.data || []));
  }, []);

  const addContact = () => {
    setForm({
      ...form,
      contacts: [
        ...form.contacts,
        { name: "", phone: "", email: "", primary: false },
      ],
    });
  };

  const saveVendor = async () => {
    try {
      const res = await fetch("/api/vendors", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (data.status === "success") {
        const vRes = await fetch("/api/vendors", {
          credentials: "include",
        });
        const vData = await vRes.json();
        setVendors(vData.data || []);

        setShowModal(false);
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Error saving vendor");
    }
  };

  const addRow = () => {
    setRows((previous) => [...previous, createEmptyRow()]);
  };

  const updateRow = (index, field, value) => {
    setRows((previous) =>
      previous.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const removeRow = (index) => {
    setRows((previous) => {
      const updatedRows = previous.filter((_, rowIndex) => rowIndex !== index);

      return updatedRows.length > 0 ? updatedRows : [createEmptyRow()];
    });
  };

  const toggleVendor = (id) => {
    if(poType==='direct'){setSelectedVendors([id]);return;}
    if (selectedVendors.includes(id)) {
      setSelectedVendors(selectedVendors.filter((v) => v !== id));
    } else {
      setSelectedVendors([...selectedVendors, id]);
    }
  };

  const sendRFQ = async () => {
    if(submittingRef.current)return;
    if (!departmentId) { alert('Select the requesting department'); return; }
    if (rows.length === 0 || selectedVendors.length === 0) {
      alert("Add items and select vendors");
      return;
    }
    if(poType==='direct'&&(selectedVendors.length!==1||rows.some(r=>r.rate===''||!Number.isFinite(Number(r.rate))||Number(r.rate)<0))){alert('Choose exactly one vendor and enter an agreed rate for every item');return;}
    submittingRef.current=true;setSubmitting(true);

    try {
      const payload = {
        po_type:poType,
        vendors: selectedVendors,
        department_id:Number(departmentId),
        items: rows,
        notes,
        terms,
        delivery_date: deliveryDate,
        location: user?.role === "staff" ? Number(branchId) : location,
        priority,
      };

      const res = await fetch("/api/purchase-orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === "success") {
        alert(poType==='direct'?'Direct PO submitted for approval. After approval, receive it in PO Tracking.':'PO submitted for approval. Vendors cannot see it until sending is confirmed.');
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
      alert("Server error");
    } finally {
      submittingRef.current=false;setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* HEADER */}
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h1 className="text-2xl font-serif">
              Create RFQ / Purchase Request
            </h1>
            <p className="text-sm text-gray-400">
              Define your requirements and invite quotations
            </p>
          </div>

          <PermissionButton permissionModule="purchase_orders"
            onClick={() => {
              setShowHistory(true);

              fetch("/api/admin/purchase-orders", {
                credentials: "include",
              })
                .then((res) => res.json())
                .then((data) => setPreviousOrders(data.data || []));
            }}
            className="px-5 py-2 rounded-xl border border-white/20 hover:bg-white/5"
          >
            View Previous Orders
          </PermissionButton>
        </div>

        {/* META */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex flex-wrap gap-3">
            {[['quotation','Request vendor quotations'],['direct','Direct / manual PO']].map(([value,label])=>(
              <button key={value} type="button" onClick={()=>{setPoType(value);setSelectedVendors([]);}}
                className={`rounded-xl px-4 py-3 text-sm border ${poType===value?'border-amber-400 bg-amber-400/10 text-amber-400':'border-white/10 text-gray-400'}`}>{label}</button>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-400">{poType==='direct'?'Choose one vendor and enter agreed rates and GST. Approval is still required, but no quotation or vendor delivery action is needed.':'Invite vendors, compare quotations and finalize a supplier before receiving goods.'}</p>
        </div>
        <section className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div>
              <h2 className="font-serif text-lg">Order Details</h2>
              <p className="mt-1 text-xs text-gray-400">Choose who is requesting the purchase and when it is needed.</p>
            </div>
            <span className="rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1 text-xs text-amber-400">Approval required before sending</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <label className="block text-sm space-y-2">
            <span className="text-gray-400">Delivery date</span>
          <input
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-[#0b0b0f] border border-white/10"
          />
          </label>
          <label className="block text-sm space-y-2">
            <span className="text-gray-400">Receiving branch</span>
          {user?.role === "staff" ? (
            <div className="px-4 py-3 rounded-xl bg-[#0b0b0f] border border-white/10 text-sm text-gray-300">
              {branches.find((b) => b.id === Number(branchId))?.name ||
                "Loading..."}
            </div>
          ) : (
            <SearchableSelect
              aria-label="Receiving branch"
              value={location}
              onChange={(e) => setLocation(Number(e.target.value))}
              className="w-full px-4 py-3 rounded-xl bg-[#0b0b0f] border border-white/10"
            >
              <option value="">Select Branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </SearchableSelect>
          )}
          </label>
          <DepartmentSelect value={departmentId} onChange={setDepartmentId} branchId={location || branchId} label="Requesting department" />
          <label className="block text-sm space-y-2">
            <span className="text-gray-400">Priority</span>
          <SearchableSelect
            aria-label="PO priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-[#0b0b0f] border border-white/10"
          >
            <option>Low</option>
            <option>Normal</option>
            <option>High</option>
            <option>Urgent</option>
          </SearchableSelect>
          </label>
          </div>
          <p className="mt-4 text-xs text-gray-500">The requesting department owns this purchase request. The PO number is assigned when you save it.</p>
        </section>

        {/* ITEMS */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h3 className="font-serif text-lg mb-4">Item Requirements</h3>

          <div className="space-y-4">
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-1 gap-3 rounded-xl border border-white/10 bg-black/20 p-4 md:grid-cols-3 xl:grid-cols-4">
                <Select
                  aria-label={`Search PO item ${i+1}`}
                  inputId={`po-item-${i}`}
                  isSearchable
                  isClearable
                  placeholder="Search item…"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  maxMenuHeight={260}
                  options={rawMaterials.map(material=>({value:material.id,label:`${material.name} · ${material.category||'Uncategorized'} · ${material.branch||''} · Stock ${material.stock}`}))}
                  value={rawMaterials.filter(material=>Number(material.id)===Number(row.item_id)).map(material=>({value:material.id,label:material.name}))[0]||null}
                  noOptionsMessage={()=>'No inventory items match your search'}
                  styles={{control:(base,state)=>({...base,background:'#111620',borderColor:state.isFocused?'#fbbf24':'#ffffff26',borderRadius:8,minHeight:42,boxShadow:'none'}),menu:base=>({...base,background:'#111620',border:'1px solid #344054'}),menuPortal:base=>({...base,zIndex:2000}),option:(base,state)=>({...base,background:state.isFocused?'#283347':'#111620',color:'#e2e8f0',fontSize:13}),singleValue:base=>({...base,color:'#e2e8f0'}),input:base=>({...base,color:'#e2e8f0'}),placeholder:base=>({...base,color:'#94a3b8'})}}
                  onChange={(option) => {
                    const itemId = Number(option?.value);

                    const material = rawMaterials.find((m) => m.id === itemId);

                    if (!material) {
                      updateRow(i, "item_id", "");
                      updateRow(i, "item", "");
                      updateRow(i, "category", "");
                      return;
                    }

                    setRows((previous) =>
                      previous.map((currentRow, rowIndex) =>
                        rowIndex === i
                          ? {
                              ...currentRow,
                              item_id: material.id,
                              item: material.name,
                              category: material.category || "",
                              unit: material.unit || currentRow.unit || "Kg",
                            }
                          : currentRow,
                      ),
                    );
                  }}
                />
                <input
                  value={row.category}
                  readOnly
                  className="px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-gray-400"
                />
                <input
                  placeholder="Spec"
                  value={row.spec}
                  onChange={(e) => updateRow(i, "spec", e.target.value)}
                  className="px-3 py-2 rounded-lg bg-black/40 border border-white/10"
                />
                <input
                  type="number"
                  placeholder="Qty"
                  value={row.qty}
                  onChange={(e) => updateRow(i, "qty", e.target.value)}
                  className="px-3 py-2 rounded-lg bg-black/40 border border-white/10"
                />
                <SearchableSelect
                  aria-label={`Unit for PO item ${i+1}`}
                  value={row.unit}
                  onChange={(e) => updateRow(i, "unit", e.target.value)}
                  className="px-3 py-2 rounded-lg bg-black/40 border border-white/10"
                >
                  {UNIT_OPTIONS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
                </SearchableSelect>
                <input
                  placeholder="Quality"
                  value={row.quality}
                  onChange={(e) => updateRow(i, "quality", e.target.value)}
                  className="px-3 py-2 rounded-lg bg-black/40 border border-white/10"
                />
                <input
                  placeholder="Packaging"
                  value={row.packaging}
                  onChange={(e) => updateRow(i, "packaging", e.target.value)}
                  className="px-3 py-2 rounded-lg bg-black/40 border border-white/10"
                />
                <input
                  placeholder="Brand"
                  value={row.brand}
                  onChange={(e) => updateRow(i, "brand", e.target.value)}
                  className="px-3 py-2 rounded-lg bg-black/40 border border-white/10"
                />
                {poType==='direct'&&<>
                  <label className="text-xs text-gray-400">Agreed rate (before GST)
                    <input aria-label={`Rate for item ${i+1}`} type="number" min="0" step="0.0001" value={row.rate} onChange={e=>updateRow(i,'rate',e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10" />
                  </label>
                  <label className="text-xs text-gray-400">GST %
                    <input aria-label={`GST for item ${i+1}`} type="number" min="0" max="100" step="0.0001" value={row.tax_percent} onChange={e=>updateRow(i,'tax_percent',e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10" />
                  </label>
                </>}
                <PermissionButton permissionAction="form" permissionModule="purchase_orders"
                  onClick={() => removeRow(i)}
                  className="text-red-400 text-sm"
                >
                  Remove
                </PermissionButton>
              </div>
            ))}
          </div>

          <PermissionButton permissionModule="purchase_orders"
            onClick={addRow}
            className="mt-4 text-amber-400 hover:underline text-sm"
          >
            + Add Another Item
          </PermissionButton>
        </div>

        {/* VENDORS */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <div className="flex justify-between items-center">
            <h3 className="font-serif text-lg mb-4">Select Vendors</h3>
            <PermissionButton permissionModule="purchase_orders"
              onClick={() => setShowModal(true)}
              className="px-8 py-2 mb-4 rounded-xl bg-amber-400 text-black font-medium"
            >
              Add Vendors
            </PermissionButton>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input aria-label="Search PO vendors" placeholder="Search vendor name, email or GST…" value={vendorSearch} onChange={e=>setVendorSearch(e.target.value)} className="md:col-span-3 w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10"/>
            {vendors.filter(v=>[v.name,v.email,v.gst].some(x=>String(x||'').toLowerCase().includes(vendorSearch.toLowerCase()))).map((v) => (
              <label
                key={v.id}
                className="flex items-center gap-3 bg-black/40 border border-white/10 p-4 rounded-xl cursor-pointer"
              >
                <input
                  type={poType==='direct'?'radio':'checkbox'}
                  name="po-vendor"
                  checked={selectedVendors.includes(v.id)}
                  onChange={() => toggleVendor(v.id)}
                />
                <div>
                  <div>
                    <p className="font-medium">{v.name}</p>
                    <p className="text-xs text-gray-400">{v.type?.name}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* NOTES */}
        <div>
          <textarea
            placeholder="Instructions for vendors..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full min-h-[100px] px-4 py-3 rounded-xl bg-black/40 border border-white/10"
          />
        </div>

        {/* TERMS & CONDITIONS */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h3 className="font-serif text-lg mb-2">Terms & Conditions</h3>
          <p className="text-xs text-gray-400 mb-3">
            These terms will be shared with vendors for this PO
          </p>
          <PermissionButton permissionModule="purchase_orders"
            onClick={() => setTerms(DEFAULT_TERMS)}
            className="text-xs text-amber-400 hover:underline mb-2"
          >
            Reset to default terms
          </PermissionButton>

          <textarea
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            placeholder="Type or paste terms & conditions here..."
            className="w-full min-h-[160px] px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-sm"
          />
        </div>

        {/* ACTION */}
        <div className="flex justify-end">
          <PermissionButton permissionModule="purchase_orders"
            onClick={sendRFQ}
            disabled={submitting}
            className="px-8 py-3 rounded-xl bg-amber-400 text-black font-medium"
          >
            {submitting?'Submitting…':'Submit for approval'}
          </PermissionButton>
        </div>
      </div>

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <h2 className="text-xl font-serif mb-6">Add Vendor</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <input
              placeholder="Vendor Name"
              className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              placeholder="Email"
              className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              placeholder="Password"
              className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />

            <SearchableSelect
              aria-label="Vendor type"
              value={form.vendor_type_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  vendor_type_id: Number(e.target.value),
                })
              }
            >
              <option value="">Select Type</option>
              {vendorTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </SearchableSelect>

            <input
              placeholder="GST Number"
              className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
            />

            <input
              placeholder="PAN"
              className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
            />
          </div>

          <p className="text-xs text-gray-400 mb-2">Contacts</p>

          <div className="space-y-3">
            {form.contacts.map((c, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  value={c.name}
                  onChange={(e) => {
                    const copy = [...form.contacts];
                    copy[i].name = e.target.value;
                    setForm({ ...form, contacts: copy });
                  }}
                />
                <input
                  value={c.phone}
                  onChange={(e) => {
                    const copy = [...form.contacts];
                    copy[i].phone = e.target.value;
                    setForm({ ...form, contacts: copy });
                  }}
                />
                <input
                  value={c.email}
                  onChange={(e) => {
                    const copy = [...form.contacts];
                    copy[i].email = e.target.value;
                    setForm({ ...form, contacts: copy });
                  }}
                />
                <PermissionButton permissionModule="purchase_orders" onClick={addContact} className="text-amber-400 text-sm">
                  + Add More
                </PermissionButton>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 mt-6 mb-2">Branch Coverage</p>

          <div className="flex flex-wrap gap-3">
            {user?.role === "staff" ? (
              <div className="text-sm text-gray-300">
                Assigned Branch:{" "}
                {branches.find((b) => b.id === Number(branchId))?.name ||
                  "Loading..."}
              </div>
            ) : (
              branches.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.branches.includes(b.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setForm({
                          ...form,
                          branches: [...form.branches, b.id],
                        });
                      } else {
                        setForm({
                          ...form,
                          branches: form.branches.filter((x) => x !== b.id),
                        });
                      }
                    }}
                  />
                  {b.name}
                </label>
              ))
            )}
          </div>

          <div className="flex justify-end gap-4 mt-8">
            <PermissionButton permissionModule="purchase_orders"
              onClick={() => setShowModal(false)}
              className="px-6 py-2 rounded-xl border border-white/20"
            >
              Cancel
            </PermissionButton>
            <PermissionButton permissionModule="purchase_orders"
              onClick={saveVendor}
              className="px-6 py-2 rounded-xl bg-amber-400 text-black font-medium"
            >
              Save Vendor
            </PermissionButton>
          </div>
        </Modal>
      )}

      {showHistory && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="w-full max-w-4xl bg-[#0b0b0f] p-8 rounded-2xl border border-white/10 relative">
            <PermissionButton permissionModule="purchase_orders"
              onClick={() => setShowHistory(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              ✕
            </PermissionButton>

            <h2 className="text-xl font-serif mb-6">
              Previous Purchase Orders
            </h2>

            <div className="space-y-4">
              {previousOrders.map((po) => (
                <div
                  key={po.id}
                  className="rounded-xl border border-white/10 bg-white/5 p-5"
                >
                  <p className="font-medium">{po.po_number}</p>
                  <p className="text-sm text-gray-400">
                    Created: {new Date(po.created_at).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-amber-400">{po.status}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-3xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative">
        <PermissionButton permissionModule="purchase_orders"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </PermissionButton>
        {children}
      </div>
    </div>
  );
}
